// Per-pest config for the pest dashboard family. Every pest dashboard is the same two
// widgets (pest-timing chart + historical trap counts) differing only by which layers are
// enabled — so the per-pest differences live here as declarative flags rather than as
// scattered `if (insectId === ...)` branches. Add a pest = add a config entry.
//
// Degree Days + Biofix are present on every pest, so they aren't flags. The variable layers
// (trap catches, applied sprays, spray windows, crop events) match the DEV-3889 test-case matrix
// and the legacy customer-web-app pest pages.

export type TPestCropEvent = 'almondBloom' | 'almondHullSplit'

export type TPestLayers = {
  trapCatches: boolean
  appliedSprays: boolean
  // Optimal spray-window bands. Only CM (1), OFM (7), OBLR (11) have a phenology model
  // (insectPhenology_<id>), so it's only true for those — enabling it elsewhere renders nothing.
  sprayWindows: boolean
  // Almond crop-event markers (1% bloom, full bloom, hull split). Almond-property value types, so
  // only the almond pest (NOW) surfaces them.
  cropEvents: TPestCropEvent[]
}

export type TPestDashboardConfig = {
  id: string
  insectId: number
  name: string
  layers: TPestLayers
  // Pests whose Semios windows are fixed life-stage cumulative-degree-day ranges rather than
  // API-driven (OBLR). When present, these are synthesized as Semios spray windows (gated on the
  // property's semios model) and the band reads "Semios Management Window" instead of
  // "Semios Spray Window". DD values are the model's native unit, mapped to dates via the
  // property's modeled flight predictions.
  customSemiosWindows?: { startDD: number; endDD: number }[]
}

export const PEST_CONFIGS: TPestDashboardConfig[] = [
  {
    id: 'pest-now',
    insectId: 6,
    name: 'Navel Orangeworm',
    layers: {
      trapCatches: true,
      appliedSprays: true,
      sprayWindows: false,
      cropEvents: ['almondBloom', 'almondHullSplit'],
    },
  },
  {
    id: 'pest-cm',
    insectId: 1,
    name: 'Codling Moth',
    layers: {
      trapCatches: true,
      appliedSprays: true,
      sprayWindows: true,
      cropEvents: [],
    },
  },
  {
    id: 'pest-ofm',
    insectId: 7,
    name: 'Oriental Fruit Moth',
    layers: {
      trapCatches: true,
      appliedSprays: true,
      sprayWindows: true,
      cropEvents: [],
    },
  },
  {
    id: 'pest-oblr',
    insectId: 11,
    name: 'Obliquebanded Leafroller',
    layers: {
      trapCatches: true,
      appliedSprays: true,
      sprayWindows: true,
      cropEvents: [],
    },
    // Hardcoded life-stage management windows (mirrors the Hub insectPhenology chart): Larvae
    // (overwintering), Eggs, Larvae, Eggs + larvae.
    customSemiosWindows: [
      { startDD: 50, endDD: 106 },
      { startDD: 400, endDD: 459 },
      { startDD: 500, endDD: 556 },
      { startDD: 1000, endDD: 1111 },
    ],
  },
  {
    id: 'pest-crs',
    insectId: 10,
    name: 'California Red Scale',
    layers: {
      trapCatches: true,
      appliedSprays: true,
      sprayWindows: false,
      cropEvents: [],
    },
  },
  // Base layers only (trap catches, degree days, biofix, applied sprays). None of these have a
  // phenology model (so no spray windows) and none are almond pests (so no crop events) — confirmed
  // against the legacy customer-web-app pest pages, which show the same base-only set for them.
  {
    id: 'pest-vmb',
    insectId: 19,
    name: 'Vine Mealybug',
    layers: {
      trapCatches: true,
      appliedSprays: true,
      sprayWindows: false,
      cropEvents: [],
    },
  },
  {
    id: 'pest-plr',
    insectId: 12,
    name: 'Pandemis Leafroller',
    layers: {
      trapCatches: true,
      appliedSprays: true,
      sprayWindows: false,
      cropEvents: [],
    },
  },
  {
    id: 'pest-ptb',
    insectId: 9,
    name: 'Peach Twig Borer',
    layers: {
      trapCatches: true,
      appliedSprays: true,
      sprayWindows: false,
      cropEvents: [],
    },
  },
  {
    id: 'pest-whf',
    insectId: 15,
    name: 'Walnut Husk Fly',
    layers: {
      trapCatches: true,
      appliedSprays: true,
      sprayWindows: false,
      cropEvents: [],
    },
  },
]
